import { IContentWithImage } from '@/utils/types';
import { Box, Spinner, Text } from '@everlywell/ui-kit';
import React, { Suspense, LazyExoticComponent } from 'react';

// Import lazy components
const StatsSection = React.lazy(() => import('components/StatsBar'));
const HowItWorksSection = React.lazy(
  () => import('components/EverlyCarePage/HowItWorks'),
);
const ProductCardGrid = React.lazy(() =>
  import('components/ProductCardGrid').then((module) => ({
    default: module.ProductCardGrid,
  })),
);
const MemberComparisonSection = React.lazy(
  () => import('containers/HealthPlansPage/MemberComparisonSection'),
);
const StepCardListSection = React.lazy(() => import('components/StepCardList'));
const ImageSection = React.lazy(() => import('components/ImageSection'));
const LogoGrid = React.lazy(() => import('components/LogoGrid'));

// Component mapping
const components: {
  StatsSection: LazyExoticComponent<React.FC<any>>;
  HowItWorksSection: LazyExoticComponent<React.FC<any>>;
  ProductCardGrid: LazyExoticComponent<React.FC<any>>;
  StepCardList: LazyExoticComponent<React.FC<any>>;
  ImageSection: LazyExoticComponent<React.FC<any>>;
  LogoGrid: LazyExoticComponent<React.FC<any>>;
  MemberComparisonSection: LazyExoticComponent<React.FC<any>>;
} = {
  StatsSection,
  HowItWorksSection,
  ProductCardGrid,
  StepCardList: StepCardListSection,
  ImageSection,
  LogoGrid,
  MemberComparisonSection,
};

// Helper functions
const isProductCardGrid = (name: string): boolean => name === 'ProductCardGrid';
const isStatsBar = (name: string): boolean => name === 'StatsSection';
const isStepCardList = (name: string): boolean => name === 'StepCardList';

type SectionProps = {
  componentName: string;
  headline: string;
  backgroundColor?: string;
  disclaimer?: {
    childMarkdownRemark: {
      rawMarkdownBody: string;
      html: string;
    };
  };
  description?: {
    description?: string;
    childMarkdownRemark: {
      rawMarkdownBody: string;
    };
  };
  slug: string;
  title?: string;
  listOfContentWithImages?: IContentWithImage[];
  container?: any;
};

type ProductSectionsProps = {
  sections: SectionProps[];
};

const ProductSections: React.FC<ProductSectionsProps> = ({ sections }) => {
  if (!sections || sections.length === 0) {
    return (
      <Box
        as="section"
        pt={['2rem', '3rem']}
        mb="3rem"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Text color="gray.500" fontSize="lg">
          No additional sections available for this product
        </Text>
      </Box>
    );
  }

  return (
    <>
      {sections.map((section, i) => {
        const Component =
          components[section.componentName as keyof typeof components];

        if (!Component) {
          return null;
        }

        if (isStatsBar(section.componentName)) {
          return (
            <Box
              key={`${section.componentName}-${i}`}
              pt={['4rem', '4rem', '6rem']}
            >
              <Suspense fallback={<Spinner size="xl" color="#28724f" />}>
                <StatsSection
                  stats={section.container?.[0]?.statsCollection || []}
                  disclaimer={section.disclaimer}
                  backgroundColor={section?.backgroundColor || '#ffffff'}
                  textColor={section.container?.[0]?.textColor}
                  labelColor={section.container?.[0]?.labelColor}
                />
              </Suspense>
            </Box>
          );
        }

        if (isStepCardList(section.componentName)) {
          return (
            <Suspense
              key={`${section.componentName}-${i}`}
              fallback={<Spinner size="xl" color="#28724f" />}
            >
              <StepCardListSection
                {...section}
                listOfContentWithImages={section.listOfContentWithImages ?? []}
                eyebrow={section.title || ''}
                heading={section.headline}
                description={section.description}
              />
            </Suspense>
          );
        }

        if (isProductCardGrid(section.componentName)) {
          return (
            <Suspense
              key={`${section.componentName}-${i}`}
              fallback={<Spinner size="xl" color="#28724f" />}
            >
              <ProductCardGrid
                title={section.title ?? ''}
                cards={section.container || []}
                backgroundColor={section.backgroundColor}
                sectionHeader={{
                  eyebrow: section.title || '',
                  title: section.headline || '',
                  headline: section.headline || '',
                  description:
                    section.description?.childMarkdownRemark?.rawMarkdownBody ||
                    '',
                }}
                showOverlay={false}
                cardsPerRow={3}
              />
            </Suspense>
          );
        }

        if (section.componentName === 'LogoGrid') {
          return (
            <Box
              key={`${section.componentName}-${i}`}
              transform={[
                'translateY(0rem)',
                'translateY(-6rem)',
                'translateY(-7rem)',
              ]}
            >
              <Suspense fallback={<Spinner size="xl" color="#28724f" />}>
                <LogoGrid
                  listOfContentWithImages={
                    section.listOfContentWithImages ?? []
                  }
                />
              </Suspense>
            </Box>
          );
        }

        if (section.componentName === 'MemberComparisonSection') {
          return (
            <Suspense
              key={`${section.componentName}-${i}`}
              fallback={<Spinner size="xl" color="#28724f" />}
            >
              <MemberComparisonSection
                title={section.title || ''}
                headline={section.headline || ''}
                disclaimer={section.disclaimer}
                container={section.container || []}
              />
            </Suspense>
          );
        }

        return (
          <Suspense
            key={`${section.componentName}-${i}`}
            fallback={<Spinner size="xl" color="#28724f" />}
          >
            {/* @ts-ignore */}
            <Component {...section} />
          </Suspense>
        );
      })}
    </>
  );
};

export default ProductSections;
